import PropTypes from 'prop-types'
import { useEffect, useRef, useState } from 'react'
import { Box, CircularProgress, Typography } from '@mui/material'
import { VideocamOffRounded } from '@mui/icons-material'
import useSmartTranslation from '../hooks/useSmartTranslation/useSmartTranslation'

/**
 * Camera-based QR / barcode reading. Isolated from QrScannerDialog so the whole decoding stack stays
 * in one file: @zxing/browser is imported dynamically, keeping it out of the main bundle for the (usual)
 * case where the operator scans with a physical reader.
 *
 * Mounting this component starts the camera; unmounting stops it. The parent only mounts it when the
 * operator explicitly asks for the camera — never automatically, so no permission prompt and no camera
 * LED unless it is really needed.
 */

// Distinguish the failure modes an operator can actually act on, instead of a silent dead video box.
const cameraErrorCode = (error) => {
  const name = String((error && error.name) || '')
  if (name === 'NotAllowedError' || name === 'SecurityError') return 'denied'
  if (name === 'NotFoundError' || name === 'OverconstrainedError') return 'notFound'
  if (name === 'NotReadableError' || name === 'TrackStartError') return 'inUse'
  return 'unknown'
}

const QrCameraScanner = ({ onDecode, height = '16rem' }) => {
  const { t_ } = useSmartTranslation()
  const videoRef = useRef(null)
  const onDecodeRef = useRef(onDecode)
  onDecodeRef.current = onDecode
  const [starting, setStarting] = useState(true)
  const [errorCode, setErrorCode] = useState('')

  useEffect(() => {
    let controls = { stop: () => {} }
    let cancelled = false

    const fail = (code) => {
      if (cancelled) return
      setErrorCode(code)
      setStarting(false)
    }

    const start = async () => {
      // getUserMedia only exists in a secure context: HTTPS or localhost. A plant terminal served over
      // plain HTTP can never use the camera, so say so instead of failing silently.
      if (!window.isSecureContext || !navigator.mediaDevices) {
        fail('insecure')
        return
      }
      try {
        const { BrowserMultiFormatReader } = await import('@zxing/browser')
        if (cancelled) return
        const reader = new BrowserMultiFormatReader(undefined, {
          delayBetweenScanAttempts: 150,
          delayBetweenScanSuccess: 1500 // don't re-fire the same label while it stays in frame
        })
        // 'ideal' (not 'exact') environment camera: the back camera on tablets/phones, and it degrades
        // gracefully to the only webcam on a desktop terminal.
        controls = await reader.decodeFromConstraints(
          { video: { facingMode: { ideal: 'environment' } } },
          videoRef.current,
          (result) => {
            // Errors here are the per-frame "nothing found yet" ones — ignore them, keep scanning.
            if (result) onDecodeRef.current(result.getText())
          }
        )
        if (cancelled) {
          controls.stop()
          return
        }
        setStarting(false)
      } catch (error) {
        fail(cameraErrorCode(error))
      }
    }

    start()
    // Stopping is mandatory: without it the stream stays open and the camera LED stays on.
    return () => {
      cancelled = true
      controls.stop()
    }
  }, [])

  const errorMessages = {
    insecure: t_('The camera needs a secure connection (HTTPS). Use the reader or type the code.'),
    denied: t_('Camera permission denied. Allow it in the browser to scan.'),
    notFound: t_('No camera found on this device.'),
    inUse: t_('The camera is being used by another application.'),
    unknown: t_('The camera could not be started.')
  }

  return (
    <Box
      sx={{
        position: 'relative',
        height,
        // Same corner as the frame it swaps places with: the box does not change shape mid-gesture.
        borderRadius: '0.75rem',
        overflow: 'hidden',
        bgcolor: 'common.black',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      {/* Always mounted: zxing needs the element to attach the stream to before decoding starts. */}
      <video
        ref={videoRef}
        autoPlay
        muted
        playsInline
        data-testid='qr-camera-video'
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: errorCode === '' ? 'block' : 'none' }}
      />

      {errorCode === '' && !starting && (
        // Viewfinder: tells the operator where to aim.
        <Box
          sx={{
            position: 'absolute',
            width: '55%',
            aspectRatio: '1 / 1',
            border: '3px solid',
            borderColor: 'primary.main',
            borderRadius: 2,
            boxShadow: '0 0 0 9999px rgba(0,0,0,0.35)',
            pointerEvents: 'none'
          }}
        />
      )}

      {starting && errorCode === '' && (
        <Box sx={{ position: 'absolute', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
          <CircularProgress size={28} />
          <Typography sx={{ fontSize: 14, color: 'common.white' }}>{t_('Starting camera')}…</Typography>
        </Box>
      )}

      {errorCode !== '' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, px: 3, textAlign: 'center' }}>
          <VideocamOffRounded sx={{ fontSize: 40, color: 'text.disabled' }} />
          <Typography sx={{ fontSize: 15, color: 'text.secondary' }}>{errorMessages[errorCode]}</Typography>
        </Box>
      )}
    </Box>
  )
}

QrCameraScanner.propTypes = {
  /** Called with the decoded text of every successful read. */
  onDecode: PropTypes.func.isRequired,
  height: PropTypes.string
}

export default QrCameraScanner
